import { getAssetsByExternalIds, isDateNumber, KuxDms, KuxLinearType, KuxProgramType } from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { ParentalPinStoreFacade } from '../../../facades/parental-pin-store-facade';
import { assertPresence, errorHandling } from '../../../utils/error-handling';
import { isEqualByKeys } from '../../../utils/isEqual';
import type { AppStore } from '../../app/app-store';
import { BingeWatchingRepository } from '../../bingeWatchingRepository';
import { LastWatchedLinearStore } from '../../channelStats/last-watched-linear-store';
import { Linear, Program, ProgramStateV2 } from '../../entities';
import { creatorEntityStore } from '../../entities/entities.service';
import { MediaAsset } from '../../entities/MediaAsset';
import { PlayableAsset } from '../../entities/PlayableAsset';
import { ErrorPriority } from '../../error';
import { PlayoutStoreV2 } from '../playout/playout-store-v2';

import { PlayoutStoreV2InitArgs } from './playout-store-v2';
import {
  addDataToPlayoutLinear,
  addDataToPlayoutProgram,
  getFutureProgram,
  getLinear,
  PlayoutStoreV2Type,
} from './playout-store.service';

export interface BingeWatchingSettings {
  watchCredits?: boolean;
  watchNow?: boolean;
  timerInProgress?: boolean;
}

interface NextAssetData {
  currentAsset?: PlayableAsset;
  subAsset?: PlayableAsset;
}

export class TimeWatcher {
  static readonly PLAYER_INTERVAL = 0.1;
  static readonly TIME_TO_NEXT_PROGRAM = 60000;
  @observable nextProgramWithMosaicReplacement?: Program;
  @observable nextProgram?: Program;
  private currentPermanentProgram?: Program;
  private playoutStore: PlayoutStoreV2;
  private nextProgramMosaicLoadTime = 0;
  private nextProgramSwitchTime = 0;
  private nextProgramLoadPromise: Promise<void> | undefined = undefined;
  private nextProgramDataLoadForSwitchPromise: Promise<void> | undefined = undefined;
  private nextProgramDataLoadPromise: Promise<void> | undefined = undefined;
  private nextAssetData: NextAssetData | undefined = undefined;
  private isNeedToSwitchStream = false;
  private silentNextEpisode = false;
  private nextEpisode?: number;
  private bingeWatchingRepository?: BingeWatchingRepository;
  private dms: KuxDms;
  private parentalPinStore: ParentalPinStoreFacade;
  private lastWatchedLinear: LastWatchedLinearStore;
  private lastPlayoutParams?: PlayoutStoreV2InitArgs;
  @observable loadingNextProgram = false;
  @observable showNextButtonTime?: number;
  @observable smartNextEpisode = false;
  @observable autoNextEpisode = false;
  @observable showNextButtonDuration?: number;
  @observable showSeriesPage = false;

  private get isMosaicEnabled() {
    return this.dms.mosaicInfo.isMosaicSupported;
  }

  constructor() {
    this.playoutStore = container.resolve<PlayoutStoreV2>('playoutStore');
    const appStore = container.resolve<AppStore>('appStore');
    this.lastWatchedLinear = appStore.lastWatchedLinear;
    this.dms = appStore.dms;
    this.parentalPinStore = appStore.parentalPinStore;
  }

  private async initBingeWatching() {
    const currentAsset = this.playoutStore.currentAsset;
    const bingeWatchingRepository = assertPresence(this.bingeWatchingRepository);

    if (!currentAsset) {
      return;
    }

    if (bingeWatchingRepository.returnSeriesPage(currentAsset)) {
      this.showSeriesPage = true;
      return;
    } else {
      this.showSeriesPage = false;
    }

    this.smartNextEpisode = bingeWatchingRepository.smartNextEpisode(currentAsset);
    this.autoNextEpisode = bingeWatchingRepository.autoNextEpisode(currentAsset);
    this.silentNextEpisode = bingeWatchingRepository.silentNextEpisode(currentAsset);

    this.showNextButtonDuration = bingeWatchingRepository.showNextButtonDuration;
  }

  async init() {
    this.bingeWatchingRepository = new BingeWatchingRepository(this.dms);
    this.setNextProgramMosaicLoadTime();
    await this.initBingeWatching();
    this.nextEpisode = undefined;
  }

  private setNextProgramMosaicLoadTime() {
    if (this.isMosaicEnabled) {
      const { mosaicPlayerRandomSecondsTime } = this.dms.mosaicInfo;
      this.nextProgramMosaicLoadTime = Math.floor(Math.random() * mosaicPlayerRandomSecondsTime) * 1000;
    }
  }

  private setNextProgramSwitchTime() {
    if (this.isMosaicEnabled) {
      const { mosaicPlayerRandomSecondsTime } = this.dms.mosaicInfo;
      this.nextProgramSwitchTime = Math.floor(Math.random() * mosaicPlayerRandomSecondsTime) * 1000;
    }
  }

  private async loadNextProgramWithMosaicReplacement(currentProgram: Program) {
    const endDate = currentProgram?.data.endDate;
    if (endDate) {
      let nextProgram = await getFutureProgram(
        currentProgram.data.linearAssetId,
        endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM,
        this.isMosaicEnabled,
      );
      let currentPermanentProgram;

      if (
        (!nextProgram || nextProgram?.data.mosaicInfo?.isMosaicEvent === '0') &&
        currentProgram?.data.mosaicInfo?.sourceProgramExternalId
      ) {
        const permanentCurrentProgram = (
          await lastValueFrom(getAssetsByExternalIds([currentProgram.data.mosaicInfo.sourceProgramExternalId]))
        )?.[0] as KuxProgramType;
        nextProgram = await getFutureProgram(
          permanentCurrentProgram.linearAssetId,
          endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM,
        );
        currentPermanentProgram = await getFutureProgram(
          permanentCurrentProgram.linearAssetId,
          endDate - TimeWatcher.TIME_TO_NEXT_PROGRAM,
        );
      }

      if (!nextProgram) {
        return;
      }

      this.nextProgramWithMosaicReplacement = nextProgram;
      this.currentPermanentProgram = currentPermanentProgram;
      this.isNeedToSwitchStream =
        Boolean(this.nextProgramWithMosaicReplacement?.data.mosaicInfo) &&
        this.nextProgramWithMosaicReplacement?.data.mosaicInfo?.isMosaicEvent !==
          currentProgram?.data.mosaicInfo?.isMosaicEvent;
    }
    this.nextProgramLoadPromise = undefined;
  }

  private startNextProgramLoad(currentProgram?: Program) {
    if (
      this.isMosaicEnabled &&
      currentProgram &&
      !this.nextProgramWithMosaicReplacement &&
      !this.nextProgramLoadPromise
    ) {
      this.nextProgramLoadPromise = this.loadNextProgramWithMosaicReplacement(currentProgram);
    }
  }

  private async scheduleNextProgramSwitch() {
    if (this.nextProgramLoadPromise) {
      await this.nextProgramLoadPromise;
    }

    if (this.isMosaicEnabled && this.isNeedToSwitchStream && !this.nextProgramDataLoadForSwitchPromise) {
      this.setNextProgramSwitchTime();
      this.nextProgramDataLoadForSwitchPromise = this.loadNextProgramDataForSwitch(
        this.nextProgramWithMosaicReplacement,
      );
    }
  }

  private async switchToNextProgram() {
    if (this.isMosaicEnabled && this.nextProgramDataLoadForSwitchPromise) {
      await this.nextProgramDataLoadForSwitchPromise;
      this.nextProgramDataLoadForSwitchPromise = undefined;

      if (this.nextAssetData) {
        this.playoutStore.subAsset = this.nextAssetData.subAsset;
        this.playoutStore.currentAsset = this.nextAssetData.currentAsset;
        this.nextAssetData = undefined;
        this.nextProgramWithMosaicReplacement = undefined;
        this.setNextProgramMosaicLoadTime();
      }
    }
  }

  /**
   * Stops playing the player
   * @private
   */
  @action
  private goBack() {
    this.playoutStore.currentAsset = undefined;
    this.playoutStore.type = undefined;
  }

  /**
   * Autoplay program on catchup or startover mode (live if catchup is not available)
   * @param timestamp
   * @param linearId
   */
  @errorHandling({ type: ErrorPriority.high })
  private async startNextProgram(timestamp: number, linearId: number, loadMosaicProgram?: boolean) {
    if (this.loadingNextProgram || !linearId) {
      return;
    }
    this.loadingNextProgram = true;
    const currentProgram = this.playoutStore.currentAsset as Program;
    const endDate = currentProgram?.data.endDate;
    let nextProgram: Program | undefined;

    if (endDate) {
      nextProgram = await getFutureProgram(currentProgram.data.linearAssetId, timestamp, this.isMosaicEnabled);

      if (nextProgram?.data.mosaicInfo?.mosaicChannelsInfo) {
        const firstChannel = nextProgram?.data.mosaicInfo.mosaicChannelsInfo[0];

        if (!firstChannel) {
          throw new Error('PlayoutStoreV2 loadNextProgramDataForSwitch - no mosaic channels');
        }

        nextProgram = creatorEntityStore(
          (await lastValueFrom(getAssetsByExternalIds([firstChannel.programExternalId])))?.[0] as KuxProgramType,
        );
      }

      if (
        (!nextProgram || nextProgram?.data.mosaicInfo?.isMosaicEvent === '0') &&
        currentProgram?.data.mosaicInfo?.sourceProgramExternalId
      ) {
        const permanentCurrentProgram = (
          await lastValueFrom(getAssetsByExternalIds([currentProgram.data.mosaicInfo.sourceProgramExternalId]))
        )?.[0] as KuxProgramType;
        nextProgram = await getFutureProgram(permanentCurrentProgram.linearAssetId, timestamp);
      }
    }

    if (nextProgram) {
      if (!nextProgram.data.enableCatchUp || nextProgram.catchupAvailableTime < Date.now()) {
        if (this.playoutStore.type === PlayoutStoreV2Type.START_OVER || this.playoutStore.isMaxiPlayer) {
          // go to live
          await this.playoutStore.init({
            id: linearId,
            type: PlayoutStoreV2Type.LIVE,
            autoplayNextProgram: false,
            saveLastWatched: false,
          });
        } else {
          // go to back
          this.goBack();
        }
      } else {
        const type =
          nextProgram.programState === ProgramStateV2.PAST ? PlayoutStoreV2Type.CATCHUP : PlayoutStoreV2Type.START_OVER;
        let mosaic;

        if (
          loadMosaicProgram &&
          nextProgram?.data.mosaicInfo?.isMosaicEvent === '1' &&
          nextProgram?.data.mosaicInfo.mosaicChannelsInfo
        ) {
          mosaic = {
            externalId: nextProgram?.data.mosaicInfo.mosaicChannelsInfo[0].programExternalId,
          };
        }

        await this.playoutStore.init({
          id: nextProgram.id,
          type,
          autoplayNextProgram: this.playoutStore.autoplayNextProgram,
          fromAutoplayMode: this.playoutStore.autoplayNextProgram,
          saveLastWatched: false,
          mosaic,
          loadMosaic: !this.isNeedToSwitchStream,
        });
      }
    }

    this.loadingNextProgram = false;
  }

  private async loadNextProgramDataForSwitch(nextProgram?: Program) {
    if (this.isMosaicEnabled && this.isNeedToSwitchStream && nextProgram) {
      const isLivePlayback = this.playoutStore.type === PlayoutStoreV2Type.LIVE;
      if (nextProgram?.data.mosaicInfo?.isMosaicEvent === '1' && nextProgram?.data.mosaicInfo.mosaicChannelsInfo) {
        const firstChannel = nextProgram?.data.mosaicInfo.mosaicChannelsInfo[0];

        if (!firstChannel) {
          throw new Error('PlayoutStoreV2 loadNextProgramDataForSwitch - no mosaic channels');
        }

        await addDataToPlayoutProgram(nextProgram as Program, PlayoutStoreV2Type.LIVE);

        if (nextProgram.linear) {
          nextProgram.linear.currentProgram = nextProgram;

          const hiddenNextAsset = creatorEntityStore(
            isLivePlayback
              ? ((await lastValueFrom(getAssetsByExternalIds([firstChannel.channelExternalId])))?.[0] as KuxLinearType)
              : ((
                  await lastValueFrom(getAssetsByExternalIds([firstChannel.programExternalId]))
                )?.[0] as KuxProgramType),
          );

          if (isLivePlayback) {
            await addDataToPlayoutLinear(hiddenNextAsset as Linear);
          } else {
            await addDataToPlayoutProgram(nextProgram as Program, PlayoutStoreV2Type.LIVE);
          }

          this.nextAssetData = {
            currentAsset: isLivePlayback ? nextProgram.linear : nextProgram,
            subAsset: hiddenNextAsset as PlayableAsset,
          };
        }
      } else if (nextProgram?.data.mosaicInfo?.isMosaicEvent === '0' && this.currentPermanentProgram) {
        await addDataToPlayoutProgram(this.currentPermanentProgram as Program, PlayoutStoreV2Type.LIVE);
        if (this.currentPermanentProgram.linear) {
          this.currentPermanentProgram.linear.currentProgram = this.currentPermanentProgram;
          const nextAsset = isLivePlayback ? this.currentPermanentProgram.linear : this.currentPermanentProgram;
          this.nextAssetData = {
            currentAsset: nextAsset,
            subAsset: nextAsset,
          };
        }
      }
    }
  }

  private async startNextBingeWatching(
    currentTime: number,
    duration: number,
    bingeWatchingSettings?: BingeWatchingSettings,
  ) {
    const currentAsset = this.playoutStore.currentAsset;
    const bingeWatchingRepository = assertPresence(this.bingeWatchingRepository);

    if (
      !currentAsset ||
      !currentAsset.data.metas.SeriesID ||
      bingeWatchingSettings?.timerInProgress ||
      this.loadingNextProgram ||
      this.showSeriesPage
    ) {
      return;
    }

    this.showNextButtonTime = bingeWatchingRepository.showNextButtonTime(currentAsset, duration);

    if (bingeWatchingRepository.startTime(currentAsset, currentTime, duration, bingeWatchingSettings)) {
      this.loadingNextProgram = true;

      if (!this.nextEpisode) {
        this.nextEpisode = await bingeWatchingRepository.getNextEpisode(currentAsset, duration);
      }

      if (!this.nextEpisode || this.nextEpisode === currentAsset.id) {
        this.showSeriesPage = true;
        this.loadingNextProgram = false;
        this.nextEpisode = undefined;
        return;
      }

      await this.playoutStore.init({
        id: this.nextEpisode,
        type: PlayoutStoreV2Type.VOD,
        autoplayNextProgram: true,
      });

      await this.initBingeWatching();
      this.nextEpisode = undefined;

      this.loadingNextProgram = false;
    }
  }

  /**
   *
   * @param currentTime - progress in seconds from player
   * @param duration - duration of stream in seconds (from manifest)
   * @param bingeWatchingSettings
   */
  @action
  @errorHandling()
  setCurrentTime = async (
    currentTime: number,
    duration: number,
    bingeWatchingSettings?: BingeWatchingSettings,
  ): Promise<void> => {
    if (this.playoutStore.isLoading) {
      return;
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.LIVE) {
      let currentAsset = this.playoutStore.currentAsset as Linear;
      const durationMs = duration * 1000;
      const currentTimeMs = currentTime * 1000;
      const endDate = currentAsset.currentProgram?.data.endDate;
      const endProgramIn = endDate ? endDate + (durationMs - currentTimeMs) : undefined;
      const isSwitchingToPermanent =
        this.isNeedToSwitchStream &&
        currentAsset.currentProgram?.data.mosaicInfo?.isMosaicEvent === '1' &&
        currentAsset.currentProgram?.data.mosaicInfo.type === 'mosaic';
      const isLoadingNextProgram = endProgramIn && Date.now() >= endProgramIn - this.nextProgramMosaicLoadTime;

      // loading of next program asset - to check if the next is mosaic
      if (isLoadingNextProgram) {
        this.startNextProgramLoad(currentAsset.currentProgram);

        if (isSwitchingToPermanent) {
          await this.scheduleNextProgramSwitch();
        }
      }

      if (endProgramIn && endProgramIn <= Date.now() && !isSwitchingToPermanent) {
        // loading of next program data (program and channel)
        await this.scheduleNextProgramSwitch();
        const playoutParams: PlayoutStoreV2InitArgs = {
          type: this.playoutStore.type,
          autoplayNextProgram: this.playoutStore.autoplayNextProgram,
          saveLastWatched: false,
          liveTimestamp: endDate ? endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM : undefined,
          loadMosaic: !this.isNeedToSwitchStream,
        };

        if (
          !this.isNeedToSwitchStream &&
          currentAsset.currentProgram?.data.mosaicInfo?.isMosaicEvent === '1' &&
          currentAsset.currentProgram?.data.mosaicInfo.type === 'mosaic' &&
          currentAsset.currentProgram?.data.mosaicInfo.mosaicChannelsInfo
        ) {
          playoutParams.externalId =
            currentAsset.currentProgram?.data.mosaicInfo.mosaicChannelsInfo[0].channelExternalId;
        } else {
          playoutParams.playableAsset = this.playoutStore.subAsset
            ? this.playoutStore.subAsset
            : this.playoutStore.currentAsset;
        }

        /**
         * Temp solution for https://kaltura.atlassian.net/browse/SUP-34966
         * skip duplicate live program requests
         */
        if (
          this.lastPlayoutParams &&
          playoutParams.externalId === this.lastPlayoutParams?.externalId &&
          playoutParams.playableAsset === this.lastPlayoutParams.playableAsset &&
          isEqualByKeys(this.lastPlayoutParams, playoutParams, [
            'type',
            'autoplayNextProgram',
            'saveLastWatched',
            'liveTimestamp',
            'loadMosaic',
          ])
        ) {
          return;
        }
        this.lastPlayoutParams = playoutParams;

        await this.playoutStore.init(playoutParams);

        if (this.nextProgramWithMosaicReplacement?.data.mosaicInfo?.isMosaicEvent === '0') {
          this.nextAssetData = undefined;
          this.nextProgramWithMosaicReplacement = undefined;
        }

        if (!this.isNeedToSwitchStream) {
          this.setNextProgramMosaicLoadTime();
        }
      }

      currentAsset = this.playoutStore.currentAsset as Linear;

      // switching to the next program
      if (
        currentAsset.currentProgram?.data.startDate &&
        (isSwitchingToPermanent
          ? Date.now() >= currentAsset.currentProgram?.data.endDate - this.nextProgramSwitchTime
          : Date.now() >= currentAsset.currentProgram?.data.startDate + this.nextProgramSwitchTime)
      ) {
        await this.switchToNextProgram();
      }

      return;
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.CATCHUP && this.playoutStore.autoplayNextProgram) {
      if (isDateNumber(currentTime)) {
        return;
      }

      // loading of next program asset
      const currentAsset = this.playoutStore.currentAsset as Program;

      if (currentAsset.linearId && duration <= currentTime + TimeWatcher.PLAYER_INTERVAL) {
        await this.startNextProgram(
          currentAsset.data.endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM,
          currentAsset.linearId,
        );
      }

      return;
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.START_OVER) {
      if (isDateNumber(currentTime)) {
        return;
      }

      const currentAsset = this.playoutStore.currentAsset as Program;
      const currentTimeMs = currentTime * 1000;

      if (
        currentAsset.linearId &&
        Math.floor(currentAsset.data.endDate - currentAsset.data.startDate) <= currentTimeMs
      ) {
        return await this.startNextProgram(
          currentAsset.data.startDate + currentTimeMs + TimeWatcher.PLAYER_INTERVAL,
          currentAsset.linearId,
        );
      }

      return;
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.VOD && !this.playoutStore.isMaxiPlayer) {
      await this.startNextBingeWatching(currentTime, duration, bingeWatchingSettings);
      return;
    }
  };

  onStreamEnd = async () => {
    if (this.playoutStore.isMaxiPlayer) {
      if (this.playoutStore.type === PlayoutStoreV2Type.NPVR) {
        const currentAsset = this.playoutStore.currentAsset as Program;
        if (currentAsset.linearId) {
          await this.playoutStore.init({
            id: currentAsset.linearId,
            type: PlayoutStoreV2Type.LIVE,
            mosaic: {
              loadFirstSubStream: true,
            },
          });
        }

        return;
      }

      if (this.playoutStore.type === PlayoutStoreV2Type.VOD) {
        if (this.lastWatchedLinear.lastLinear) {
          await this.playoutStore.init({
            id: this.lastWatchedLinear.lastLinear?.id,
            type: PlayoutStoreV2Type.LIVE,
            mosaic: {
              loadFirstSubStream: true,
            },
          });
        }

        return;
      }
    }
  };

  async getNextAssetToPlay(): Promise<MediaAsset | null> {
    if (this.playoutStore.type === PlayoutStoreV2Type.LIVE) {
      const currentAsset = this.playoutStore.currentAsset as Linear;
      const endDate = currentAsset.currentProgram?.data.endDate;
      const liveTimestamp = endDate ? endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM : undefined;
      return addDataToPlayoutLinear(currentAsset, liveTimestamp, this.isMosaicEnabled);
    }

    if (
      this.playoutStore.type === PlayoutStoreV2Type.CATCHUP ||
      this.playoutStore.type === PlayoutStoreV2Type.START_OVER
    ) {
      const currentAsset = this.playoutStore.currentAsset as Program;
      if (currentAsset.linearId) {
        const nextProgram = await getFutureProgram(
          currentAsset.linearId,
          currentAsset.data.endDate + TimeWatcher.TIME_TO_NEXT_PROGRAM,
        );
        if (nextProgram) {
          return nextProgram;
        }
      }
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.NPVR) {
      const currentAsset = this.playoutStore.currentAsset as Program;

      if (currentAsset.linearId) {
        const linear = await getLinear(currentAsset.linearId);
        // @ts-expect-error TODO Fix
        await addDataToPlayoutLinear(linear, undefined, this.isMosaicEnabled);
      }
    }

    if (this.playoutStore.type === PlayoutStoreV2Type.VOD) {
      const { lastLinear } = this.lastWatchedLinear;

      if (lastLinear) {
        const linear = await getLinear(lastLinear.id);
        // @ts-expect-error TODO Fix
        await addDataToPlayoutLinear(linear, undefined, this.isMosaicEnabled);
      }
    }

    return null;
  }
}
